/** Live shopgnd.gd Cognito pool — never enable sandbox quick sign-in against this in dev. */
export const PRODUCTION_USER_POOL_ID = 'us-east-2_rxctmB6eV';

/** Latest known Amplify sandbox pool (each `ampx sandbox` may create a new pool). */
export const SANDBOX_USER_POOL_ID = 'us-east-2_PKU5jtAru';

export type RuntimeEnvironment = 'mock' | 'sandbox' | 'production' | 'local';

export type RuntimeEnvironmentInfo = {
  env: RuntimeEnvironment;
  label: string;
  detail: string;
  /** Cognito user pool from amplify_outputs.json (when loaded). */
  userPoolId?: string;
  /** Dev-only — env override in .env disagrees with the loaded Cognito pool. */
  configWarning?: string;
  /** Show a persistent banner (non-production or mock). */
  showBanner: boolean;
  bannerTone: 'amber' | 'violet' | 'rose';
};

export function isProductionUserPool(userPoolId?: string | null): boolean {
  return userPoolId === PRODUCTION_USER_POOL_ID;
}

function envFromUserPoolId(userPoolId: string, isDev: boolean): RuntimeEnvironment {
  if (userPoolId === PRODUCTION_USER_POOL_ID) return 'production';
  if (isDev) return 'sandbox';
  return 'production';
}

export function resolveRuntimeEnvironment(opts: {
  useMock: boolean;
  userPoolId?: string | null;
  isDev: boolean;
  explicitEnv?: string;
}): RuntimeEnvironmentInfo {
  if (opts.useMock) return withPoolId(buildInfo('mock', opts.isDev), opts.userPoolId);

  // amplify_outputs Cognito pool is the source of truth for auth (overrides VITE_RUNTIME_ENV).
  if (opts.userPoolId) {
    const poolEnv = envFromUserPoolId(opts.userPoolId, opts.isDev);
    const info = withPoolId(buildInfo(poolEnv, opts.isDev), opts.userPoolId);
    const override = opts.explicitEnv?.trim().toLowerCase();
    if (
      override &&
      (override === 'sandbox' || override === 'production' || override === 'mock') &&
      override !== poolEnv
    ) {
      return {
        ...info,
        configWarning: `VITE_RUNTIME_ENV=${override} is ignored — amplify_outputs.json uses the ${poolEnv} pool (${opts.userPoolId}).`,
      };
    }
    return info;
  }

  const override = opts.explicitEnv?.trim().toLowerCase();
  if (override === 'sandbox' || override === 'production' || override === 'mock') {
    return buildInfo(override as RuntimeEnvironment, opts.isDev);
  }

  if (opts.isDev) return buildInfo('local', opts.isDev);
  return buildInfo('production', opts.isDev);
}

function withPoolId(
  info: RuntimeEnvironmentInfo,
  userPoolId?: string | null,
): RuntimeEnvironmentInfo {
  return userPoolId ? { ...info, userPoolId } : info;
}

function buildInfo(env: RuntimeEnvironment, isDev: boolean): RuntimeEnvironmentInfo {
  switch (env) {
    case 'mock':
      return {
        env,
        label: 'Mock data',
        detail: 'Local seed data only — nothing is saved to AWS.',
        showBanner: true,
        bannerTone: 'violet',
      };
    case 'sandbox':
      return {
        env,
        label: 'Sandbox',
        detail: 'Amplify sandbox backend · test Stripe · outreach vendor data.',
        showBanner: true,
        bannerTone: 'amber',
      };
    case 'local':
      return {
        env,
        label: 'Local dev',
        detail: 'Development server — check amplify_outputs.json for the active backend.',
        showBanner: isDev,
        bannerTone: 'amber',
      };
    case 'production':
      return {
        env,
        label: 'Production',
        detail: isDev
          ? 'Connected to live shopgnd.gd data — changes affect real users.'
          : 'Live site',
        showBanner: isDev,
        bannerTone: 'rose',
      };
  }
}
